// The test valley's course (brief A2.2 and A2.3), as data: a walled pen of heightfield ground, flat in the middle and
// rising to low hills at the walls, with a course built from the controller's tuning so it stays a test of that
// tuning when the numbers change:
//   - a lane of steps at 40, 70 and 95 % of the step height (climbed) and one at 150 % (must stop the hero);
//   - ramps at 15° and 30° and at 90 % of the steepest slope (climbed), and one 12° past it (must stop the hero), each
//     with a plateau on top; the 30° plateau is also the ledge for coyote time;
//   - a pond: a bowl in the heightfield under a water volume, shallower than the swim depth (the hero wades);
//   - a sand patch (a surface tag that slows), an inside wall to slide along, and the stub mount's stand.
// Layers: terrain (the heightfield, alone, so the below-ground check can ray it), solid (everything else built),
// hero, mount (the hero and the mount don't collide: the rider sits inside the mount's capsule).
import type { BodySpec, LayerTable, Vec3, WaterVolume } from "@rpg/collision";
import type { ControllerTuning } from "@rpg/controller";

export const VALLEY_LAYERS: LayerTable = { terrain: [], solid: [], hero: ["terrain", "solid"], mount: ["terrain", "solid"] };

const DEG = Math.PI / 180;
/** The pen: ±HALF m, walls at ±WALL. */
const HALF = 32;
const WALL = 31;
const CELLS = 64;

/** The bowl the pond sits in. */
export const POND = { x: -12, z: 10, radius: 4.5, depth: 0.6, surface: 0 };
/** The sand patch (x0, x1, z0, z1). */
export const SAND = { x0: 4, x1: 12, z0: 8, z1: 14 };
/** The inside wall along z at x = WALL_X. */
export const INSIDE_WALL = { x: 18, z0: 4, z1: 12, height: 2 };
/** Where the hero and the mount start, and the hub the route returns to. */
export const SPAWN = { x: 0, z: 4, yaw: Math.PI };
export const MOUNT_SPAWN = { x: -4, z: 16, yaw: Math.PI };
/** The open line the feel spec measures on (flat, clear from x -24 to 24). */
export const FEEL_LINE = { x: -22, z: 20 };

const smooth = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

/** The terrain's height at (x, z): flat in the middle, hills toward the walls, the pond's bowl. */
export function terrainHeight(x: number, z: number): number {
  const r = Math.max(Math.abs(x), Math.abs(z));
  const hills = 1.5 * smooth(24, 31, r) * (0.75 + 0.25 * Math.sin(x * 0.4) * Math.cos(z * 0.3));
  const bowl = POND.depth * smooth(POND.radius, POND.radius - 2, Math.hypot(x - POND.x, z - POND.z));
  return hills - bowl;
}

/** A step: its centre x, its height, and whether the tuning allows it. */
export interface Step {
  id: string;
  x: number;
  height: number;
  allowed: boolean;
}
/** A ramp: its centre x, slope, the z of its low edge and of its plateau's centre, plateau height, allowed or not. */
export interface Ramp {
  id: string;
  x: number;
  degrees: number;
  lowZ: number;
  topZ: number;
  height: number;
  allowed: boolean;
}

export interface Course {
  bodies: BodySpec[];
  water: WaterVolume[];
  steps: Step[];
  ramps: Ramp[];
  /** z of the step lane. */
  stepLaneZ: number;
}

const STEP_HALF = 0.8;
const RAMP_HALF_W = 1.2;
const PLATEAU_DEPTH = 2;
const PLATEAU_H = 1.2;

/** Build the course from the hero's tuning. */
export function buildCourse(t: ControllerTuning): Course {
  const bodies: BodySpec[] = [];
  const heights: number[] = [];
  for (let iz = 0; iz <= CELLS; iz++) for (let ix = 0; ix <= CELLS; ix++) heights.push(terrainHeight((ix / CELLS - 0.5) * 2 * HALF, (iz / CELLS - 0.5) * 2 * HALF));
  bodies.push({ id: "terrain", kind: "fixed", colliders: [{ shape: { type: "heightfield", rows: CELLS, cols: CELLS, heights, size: { x: 2 * HALF, y: 1, z: 2 * HALF } }, layer: "terrain", tag: "grass" }] });
  const box = (id: string, c: Vec3, h: Vec3, tag: string): void => {
    bodies.push({ id, kind: "fixed", position: c, colliders: [{ shape: { type: "cuboid", halfExtents: h }, layer: "solid", tag }] });
  };
  // the pen's walls, 3 m tall over the hills
  for (const [id, x, z, hx, hz] of [
    ["wall-n", 0, -WALL, WALL, 0.4],
    ["wall-s", 0, WALL, WALL, 0.4],
    ["wall-w", -WALL, 0, 0.4, WALL],
    ["wall-e", WALL, 0, 0.4, WALL],
  ] as const) {
    box(id, { x, y: 2.5, z }, { x: hx, y: 2.5, z: hz }, "stone");
  }
  box("inside-wall", { x: INSIDE_WALL.x, y: INSIDE_WALL.height / 2, z: (INSIDE_WALL.z0 + INSIDE_WALL.z1) / 2 }, { x: 0.3, y: INSIDE_WALL.height / 2, z: (INSIDE_WALL.z1 - INSIDE_WALL.z0) / 2 }, "stone");

  // the step lane along z = -10: three the tuning allows, one it doesn't
  const stepLaneZ = -10;
  const steps: Step[] = [0.4, 0.7, 0.95, 1.5].map((f, i) => ({ id: `step-${Math.round(f * 100)}`, x: -18 + 4 * i, height: Math.round(f * t.stepHeight * 1000) / 1000, allowed: f <= 1 }));
  for (const s of steps) box(s.id, { x: s.x, y: s.height / 2, z: stepLaneZ }, { x: STEP_HALF, y: s.height / 2, z: STEP_HALF }, "stone");

  // the ramps: a solid wedge (a closed triangle mesh) rising toward -z from z = -2, a plateau box behind it
  const lowZ = -2;
  const ramps: Ramp[] = [
    { deg: 15, allowed: true },
    { deg: 30, allowed: true },
    { deg: Math.round(0.9 * t.maxSlope), allowed: true },
    { deg: Math.round(t.maxSlope + 12), allowed: false },
  ].map((r, i) => {
    const len = PLATEAU_H / Math.tan(r.deg * DEG);
    return { id: `ramp-${r.deg}`, x: 4 + 5 * i, degrees: r.deg, lowZ, topZ: lowZ - len - PLATEAU_DEPTH / 2, height: PLATEAU_H, allowed: r.allowed };
  });
  for (const r of ramps) {
    const len = PLATEAU_H / Math.tan(r.degrees * DEG);
    const w = RAMP_HALF_W;
    const z0 = 0;
    const z1 = -len;
    const H = PLATEAU_H;
    // A, B low edge; C, D high edge; E, F under the high edge
    const v = [-w, 0, z0, w, 0, z0, -w, H, z1, w, H, z1, -w, 0, z1, w, 0, z1];
    const idx = [0, 1, 3, 0, 3, 2, 4, 2, 3, 4, 3, 5, 0, 2, 4, 1, 5, 3, 0, 4, 5, 0, 5, 1];
    bodies.push({ id: r.id, kind: "fixed", position: { x: r.x, y: 0, z: r.lowZ }, colliders: [{ shape: { type: "trimesh", vertices: v, indices: idx }, layer: "solid", tag: "wood" }] });
    box(`${r.id}-top`, { x: r.x, y: H / 2, z: r.topZ }, { x: w, y: H / 2, z: PLATEAU_DEPTH / 2 }, "stone");
  }

  // the sand patch: a thin slab (a 3 cm step up, well under the step height)
  box("sand", { x: (SAND.x0 + SAND.x1) / 2, y: 0.015, z: (SAND.z0 + SAND.z1) / 2 }, { x: (SAND.x1 - SAND.x0) / 2, y: 0.015, z: (SAND.z1 - SAND.z0) / 2 }, "sand");

  const water: WaterVolume[] = [
    { id: "pond", min: { x: POND.x - POND.radius, y: -POND.depth - 0.5, z: POND.z - POND.radius }, max: { x: POND.x + POND.radius, y: POND.surface, z: POND.z + POND.radius }, surface: POND.surface, tag: "water" },
  ];
  return { bodies, water, steps, ramps, stepLaneZ };
}

/** A leg of the route: the harness's leg plus what the spike checks on it. */
export interface CourseLeg {
  name: string;
  to: { x: number; z: number };
  timeout: number;
  expect?: "reach" | "blocked";
  radius?: number;
  minY?: number;
  /** The hero must stay on the ground the whole leg (ground snap). */
  grounded?: boolean;
  /** Free legs between sections: the seeded bot may sprint, slow down and jump on them. */
  transit?: boolean;
  /** A surface tag or water the hero must meet on the leg. */
  meets?: "water" | "sand";
  /** The course element the leg tests, for the climbed / not climbed tally. */
  tests?: string;
}

/** The course's sections, each a list of legs starting and ending near the hub. */
export function courseSections(c: Course): Record<string, CourseLeg[]> {
  const z = c.stepLaneZ;
  const go = (name: string, x: number, zz: number, extra: Partial<CourseLeg> = {}): CourseLeg => ({ name, to: { x, z: zz }, timeout: 12, transit: true, ...extra });
  const steps: CourseLeg[] = [go("to the step lane", -21, -4), go("step lane start", -21, z)];
  for (const s of c.steps) {
    if (s.allowed) {
      steps.push({ name: `${s.id} up`, to: { x: s.x, z }, timeout: 4, minY: s.height - 0.05, tests: s.id });
      steps.push({ name: `${s.id} down`, to: { x: s.x + 2, z }, timeout: 4, grounded: true });
    } else {
      steps.push({ name: s.id, to: { x: s.x, z }, timeout: 1.5, expect: "blocked", minY: s.height - 0.05, tests: s.id });
    }
  }
  steps.push(go("step lane exit", -8, -4), go("back to the hub", SPAWN.x, SPAWN.z));
  const slopes: CourseLeg[] = [];
  let ledge: CourseLeg[] = [];
  for (const r of c.ramps) {
    slopes.push(go(`${r.id} foot`, r.x, 0.5));
    if (r.allowed) {
      slopes.push({ name: `${r.id} up`, to: { x: r.x, z: r.topZ }, timeout: 5, minY: r.height - 0.1, tests: r.id });
      slopes.push({ name: `${r.id} down`, to: { x: r.x, z: 0.5 }, timeout: 5, grounded: true });
      if (r.degrees === 30)
        ledge = [
          go(`${r.id} foot`, r.x, 0.5),
          { name: `${r.id} up again`, to: { x: r.x, z: r.topZ }, timeout: 5, minY: r.height - 0.1 },
          // off the plateau's side into the gap between the ramps: a drop, not a grounded leg
          { name: "off the ledge", to: { x: r.x + 2.5, z: r.topZ }, timeout: 4 },
          go("ledge foot", r.x + 2.5, 0.5),
        ];
    } else {
      slopes.push({ name: r.id, to: { x: r.x, z: r.topZ }, timeout: 2, expect: "blocked", minY: r.height * 0.5, tests: r.id });
      slopes.push(go(`${r.id} back`, r.x, 0.5));
    }
  }
  slopes.push(go("back to the hub", SPAWN.x, SPAWN.z));
  ledge.push(go("back to the hub", SPAWN.x, SPAWN.z));
  return {
    steps,
    slopes,
    ledge,
    pond: [go("pond edge", POND.x + 6, POND.z), { name: "through the pond", to: { x: POND.x - 6, z: POND.z }, timeout: 8, grounded: true, meets: "water" }, go("back to the hub", SPAWN.x, SPAWN.z)],
    sand: [go("sand edge", SAND.x0 - 1, (SAND.z0 + SAND.z1) / 2), { name: "across the sand", to: { x: SAND.x1 + 1, z: (SAND.z0 + SAND.z1) / 2 }, timeout: 6, grounded: true, meets: "sand" }, go("back to the hub", SPAWN.x, SPAWN.z)],
    // aimed through the wall at 45°: the hero slides along it and round its end
    wall: [go("wall approach", INSIDE_WALL.x - 4, INSIDE_WALL.z0 + 2), { name: "slide along the wall", to: { x: INSIDE_WALL.x + 4, z: INSIDE_WALL.z1 + 2 }, timeout: 10 }, go("wall exit", INSIDE_WALL.x + 4, 2), go("back to the hub", SPAWN.x, SPAWN.z)],
  };
}
